import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { getERPClient } from "@/lib/erp/client";
import { useDocTypeListWithPagination } from "@/features/common/api/useDocTypeListWithPagination";
import { fetchCustomerChatSummary, type CustomerChatSummary } from "./customerConversation";
import type { FilterDescriptor } from "@/lib/erp";

// Fields to fetch for customer list
export const CUSTOMER_LIST_FIELDS = [
  "name",
  "customer_name",
  "customer_type",
  "customer_group",
  "territory",
  "email_id",
  "phone",
  "mobile_no",
  "image",
  "modified",
  "creation",
] as const;

export function useCustomersList(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["customers", "list", filters],
    queryFn: () => erp.customers.list(filters),
  });
}

/**
 * Paginated customer list hook for DocTypeListPageShell
 *
 * Uses a custom backend method that LEFT JOINs Contact data to populate
 * email_id, mobile_no, and phone — which are otherwise NULL in list queries
 * because Frappe's fetch_from only triggers on single-document save.
 */
export function useCustomerListWithPagination(params: {
  filterDescriptors: FilterDescriptor[];
  searchQuery: string;
  pageIndex: number;
  pageSize: number;
  orderBy?: string;
  fields?: string[];
}) {
  return useDocTypeListWithPagination<{
    name: string;
    customer_name: string;
    customer_type?: string;
    customer_group?: string;
    territory?: string;
    email_id?: string;
    phone?: string;
    mobile_no?: string;
    image?: string;
    modified?: string;
    creation?: string;
  }>({
    doctype: "Customer",
    filterDescriptors: params.filterDescriptors,
    searchQuery: params.searchQuery,
    // NOTE: searchFields become or_filters validated against the Customer doctype,
    // so they must be REAL Customer columns. "phone" lives on Contact (not Customer)
    // → using it here crashed search with "Invalid filter field 'phone'". Use mobile_no.
    searchFields: ["customer_name", "name", "email_id", "mobile_no"],
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    orderBy: params.orderBy ?? "modified desc",
    // Always request the contact fields, even when the visible-column set (which
    // the shell derives `fields` from) omits them: `phone` isn't a real Customer
    // field so the shell drops it, and `mobile_no` isn't its own column (the
    // Phone cell reads it as a fallback), so neither would be fetched — leaving
    // every Phone cell blank. list_customers resolves all three from the primary
    // Contact. (email_id is genuinely empty for LINE/OTP customers — expected.)
    fields: Array.from(
      new Set([
        ...(params.fields ?? [...CUSTOMER_LIST_FIELDS]),
        "name",
        "customer_name",
        "email_id",
        "mobile_no",
        "phone",
      ]),
    ),
    listMethodOverride:
      "zaviago_backend.api.customer_list.list_customers",
  });
}

export function useCustomer(name: string) {
  return useQuery({
    queryKey: ["customers", "detail", name],
    queryFn: () => erp.customers.detail(name),
    enabled: !!name,
  });
}

export function useCustomerOrders(customerName: string) {
  return useQuery({
    queryKey: ["customers", "orders", customerName],
    queryFn: () => erp.customers.orders(customerName),
    enabled: !!customerName,
  });
}

export interface CustomerCart {
  name: string;
  updated: string;
  grand_total: number;
  total_qty: number;
  items: Array<{ item_code: string; item_name: string; qty: number; rate: number; amount: number }>;
  abandoned: boolean;
  age_days: number;
}

export interface CustomerBrowsingEvent {
  event: string;
  path: string | null;
  el_text?: string | null;
  at: string | null;
}

export interface CustomerActivity {
  success: boolean;
  stats: {
    order_count?: number | null;
    total_spend?: number | null;
    unpaid_count?: number | null;
    last_order?: string | null;
  };
  cart: CustomerCart | null;
  /** {order_name: product image URL} — first item image per order. */
  order_thumbs?: Record<string, string>;
  /** {collection_slug: image URL} — banner or product image per collection. */
  collection_thumbs?: Record<string, string>;
  browsing: CustomerBrowsingEvent[];
  last_seen: string | null;
}

/**
 * Cart (current + abandoned) + commerce stats + recent storefront browsing
 * (PostHog) for one customer. Everything derived server-side from the
 * permission-checked Customer doc. Browsing is best-effort (empty if PostHog off).
 */
export function useCustomerActivity(customer: string) {
  return useQuery<CustomerActivity>({
    queryKey: ["customers", "activity", customer],
    queryFn: async () => {
      const client = getERPClient();
      return client.call<CustomerActivity>(
        "zaviago_backend.api.customer_activity.get_customer_activity",
        { customer, browsing_limit: 8 },
      );
    },
    enabled: !!customer,
    staleTime: 60_000,
  });
}

/**
 * Resolve a customer's avatar URL — server picks Customer.image, else the LINE
 * profile picture (Storefront Customer Profile.line_picture_url). Reuses the
 * same backend method the orders list uses (never rebuild).
 */
export function useCustomerAvatar(customerName: string) {
  return useQuery<string | null>({
    queryKey: ["customers", "avatar", customerName],
    queryFn: async () => {
      const client = getERPClient();
      const map = await client.call<Record<string, string>>(
        "zaviago_backend.api.customer_list.get_customer_avatars",
        { customer_names: [customerName] },
      );
      return map?.[customerName] ?? null;
    },
    enabled: !!customerName,
  });
}

export interface CustomerTopProduct {
  name: string;
  qty: number;
}

/** Service / add-on line items to exclude from the "favourite product" signal. */
function isServiceItem(itemCode?: string, itemName?: string): boolean {
  const code = (itemCode || "").toUpperCase();
  const name = (itemName || "").toLowerCase();
  return code.startsWith("SVC-") || /embroidery|personaliz|ปัก/i.test(name);
}

/**
 * Aggregate a customer's most-bought products across their recent orders. The
 * Sales Order Item child table isn't independently listable (403), so we read
 * the order DOCS (which embed their items) and sum qty per product, excluding
 * service/personalization add-ons. Capped at the 12 most-recent orders. Empty
 * on failure (graceful: the card just hides the line).
 */
export function useCustomerTopProducts(orderNames: string[]) {
  const names = orderNames.slice(0, 12);
  const key = names.slice().sort().join(",");
  return useQuery<CustomerTopProduct[]>({
    queryKey: ["customers", "top-products", key],
    enabled: names.length > 0,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const client = getERPClient();
      const docs = await Promise.all(
        names.map((n) =>
          client.get<{ items?: Array<{ item_code?: string; item_name?: string; qty?: number }> }>({
            doctype: "Sales Order",
            name: n,
          }).catch(() => null),
        ),
      );
      const byItem: Record<string, CustomerTopProduct> = {};
      for (const d of docs) {
        for (const it of d?.items ?? []) {
          if (isServiceItem(it.item_code, it.item_name)) continue;
          const k = it.item_code || it.item_name || "";
          if (!k) continue;
          byItem[k] = byItem[k] || { name: it.item_name || it.item_code || k, qty: 0 };
          byItem[k].qty += Number(it.qty) || 0;
        }
      }
      return Object.values(byItem).sort((a, b) => b.qty - a.qty);
    },
  });
}

/**
 * Fetch a customer's recent Chatwoot messages (LINE-linked customers only).
 * Shared by the header chat badge + last-message preview so they dedup into a
 * single Control Plane request. Returns the last few messages, newest last.
 */
export function useCustomerChatSummary(lineUserId?: string) {
  return useQuery<CustomerChatSummary>({
    queryKey: ["customers", "chat-summary", lineUserId],
    queryFn: () => fetchCustomerChatSummary({ lineUserId, limit: 6 }),
    enabled: !!lineUserId,
    staleTime: 60_000,
    retry: false,
  });
}

export interface CustomerAddress {
  name: string;
  addressType?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  phone?: string;
  isPrimary: boolean;
  isShipping: boolean;
  /** source_channel → number of orders shipped to this address. */
  channels: Record<string, number>;
  orderCount: number;
}

/**
 * Every address this customer has used — the Address records linked to them
 * (via the Dynamic Link child table), enriched with which channels shipped to
 * each (from each order's `shipping_address_name` + `source_channel`). Lets the
 * Addresses tab show a Shopee/Lazada/Storefront breakdown per address.
 */
export function useCustomerAddresses(customer: string) {
  return useQuery<CustomerAddress[]>({
    queryKey: ["customers", "addresses", customer],
    enabled: !!customer,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const client = getERPClient();
      const [addrs, orders] = await Promise.all([
        client.list<Record<string, any>>({
          doctype: "Address",
          filters: [
            ["Dynamic Link", "link_doctype", "=", "Customer"],
            ["Dynamic Link", "link_name", "=", customer],
          ],
          fields: [
            "name", "address_type", "address_line1", "address_line2", "city",
            "state", "country", "pincode", "phone", "is_primary_address", "is_shipping_address",
          ],
          limit_page_length: 50,
        }),
        client.list<Record<string, any>>({
          doctype: "Sales Order",
          filters: { customer },
          fields: ["name", "shipping_address_name", "source_channel"],
          limit_page_length: 200,
        }),
      ]);

      const usage: Record<string, { channels: Record<string, number>; count: number }> = {};
      for (const o of orders) {
        const a = o.shipping_address_name as string | undefined;
        if (!a) continue;
        usage[a] = usage[a] || { channels: {}, count: 0 };
        usage[a].count += 1;
        const ch = (o.source_channel as string) || "Storefront";
        usage[a].channels[ch] = (usage[a].channels[ch] || 0) + 1;
      }

      const list: CustomerAddress[] = (addrs || []).map((a) => ({
        name: a.name,
        addressType: a.address_type,
        line1: a.address_line1,
        line2: a.address_line2,
        city: a.city,
        state: a.state,
        country: a.country,
        pincode: a.pincode,
        phone: a.phone,
        isPrimary: !!a.is_primary_address,
        isShipping: !!a.is_shipping_address,
        channels: usage[a.name]?.channels || {},
        orderCount: usage[a.name]?.count || 0,
      }));
      list.sort((x, y) => Number(y.isPrimary) - Number(x.isPrimary) || y.orderCount - x.orderCount);
      return list;
    },
  });
}
